import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import React, { useCallback, useEffect, useState } from 'react';
import { Image, Platform, Pressable, ScrollView, View, useWindowDimensions } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useAppDispatch, useAppSelector } from '../../../hooks/store-hook';
import useTranslation from '../../../hooks/translation-hook';
import { lightBoxActions } from '../../../store/lightBoxSlice';
import CustomButton from '../CustomButton';
import CustomCheckbox from '../CustomCheckbox';
import Label from '../Label';
import Loading from '../Loading';
import { areFlashMissionPopupAssetsReady, preloadFlashMissionPopupAssets } from '../../../helpers/popupAssetPreload';

export interface MissionIntroductionPopupProps {
  title: string;
  tagline: string;
  body: string;
  examples?: string;
  onClose?(dontShowAgain?: boolean): void;
}

const MissionIntroductionPopup = (props: MissionIntroductionPopupProps) => {
  const t = useTranslation();
  const dispatch = useAppDispatch();
  const layout = useWindowDimensions();
  const userPrefs = useAppSelector((selector) => selector.userPrefs);
  // Keep Flash's existing breakpoint without depending on CustomMap having
  // mounted (or updated the shared lightbox flag) before this route opens.
  const mobile = layout.width < 1200;
  const insets = useSafeAreaInsets();
  const lang = (userPrefs.uiLanguage?.trim() || process.env.REACT_APP_DEFAULT_LANG || 'en').toLowerCase();
  const isEn = lang.startsWith('en');
  const [checked, setChecked] = useState<boolean>(false);
  const [imagesReady, setImagesReady] = useState<boolean>(areFlashMissionPopupAssetsReady());
  const webNoDragStyle = Platform.OS === 'web' ? ({ userSelect: 'none' } as any) : undefined;
  const nativeBottomInset = Platform.OS === 'android' ? Math.max(insets.bottom, 0) : 0;
  const popupWidth = mobile ? Math.min(350, Math.max(layout.width - 24, 280)) : 625;
  const popupMaxWidth = mobile ? popupWidth : 675;
  const popupHeight = Math.min(600, Math.max(layout.height - 40 - nativeBottomInset, 0));
  const mobileHeroHeight = Math.round((popupWidth * 232) / 350);
  const desktopIllustrationWidth = 462;
  const desktopIllustrationHeight = Math.round((desktopIllustrationWidth * 992) / 924);
  const footerPadding = layout.width > 1200 ? 16 : 12;
  const footerTopPadding = layout.width > 1200 ? 16 : 0;
  const footerBottomPadding = footerPadding;
  const popupBackgroundColor = '#f5a54a';
  const popupTaglineColor = '#8e183d';
  const popupBodyColor = '#000000';
  const popupButtonColor = '#8e183d';
  const dontShowTextColor = '#000000';
  const dontShowCheckColor = '#000000';
  const dontShowBoxStyle = { backgroundColor: '#ffffff', borderColor: '#000000' };

  const imageSource = mobile
    ? isEn
      ? require('../../../../assets/quick-one-en.png')
      : require('../../../../assets/quick-one-fr.png')
    : isEn
      ? require('../../../../assets/quick-one-en-desktop.png')
      : require('../../../../assets/quick-one-fr-desktop.png');

  const handleClose = useCallback(() => {
    dispatch(lightBoxActions.setVisibility(false));
    dispatch(lightBoxActions.setOnClose(undefined));
    props.onClose && props.onClose(checked);
    setChecked(false);
  }, [checked, dispatch, props.onClose]);

  useEffect(() => {
    dispatch(lightBoxActions.setOnClose(handleClose));
  }, [dispatch, handleClose]);

  useEffect(() => {
    let mounted = true;

    preloadFlashMissionPopupAssets().then(() => {
      if (mounted) {
        setImagesReady(true);
      }
    });

    return () => {
      mounted = false;
    };
  }, []);

  const handleCheckboxChange = (checked: boolean) => {
    setChecked(checked);
  };

  const renderFooter = () => (
    <View
      style={{
        paddingHorizontal: footerPadding,
        paddingTop: footerTopPadding,
        paddingBottom: footerBottomPadding,
        backgroundColor: popupBackgroundColor,
      }}
    >
      <CustomCheckbox
        value="dontShowAgain"
        label={t('Dont-show-this-again')}
        checked={checked}
        textStyle={{ color: dontShowTextColor }}
        color={dontShowCheckColor}
        boxStyle={dontShowBoxStyle}
        onChange={handleCheckboxChange}
      />
    </View>
  );

  const renderDesktopBody = () => (
    <View
      style={{
        minHeight: 520,
        backgroundColor: popupBackgroundColor,
        position: 'relative',
        justifyContent: 'flex-end',
        paddingLeft: 36,
        paddingRight: 36,
        paddingTop: 28,
        paddingBottom: 20,
      }}
    >
      <Image
        {...({ pointerEvents: 'none' } as any)}
        source={imageSource}
        style={{
          position: 'absolute',
          top: 0,
          right: -1,
          width: desktopIllustrationWidth,
          height: desktopIllustrationHeight,
          resizeMode: 'contain',
        }}
      />
      <View style={{ maxWidth: 320, zIndex: 1, top: 40 }}>
        <Label style={{ maxWidth: 175, fontSize: 26, color: '#ffffff', fontWeight: '600' }} numberOfLines={3}>
          {props.title}
        </Label>
        <Label style={{ maxWidth: 250, marginTop: 16, fontSize: 18, color: popupTaglineColor, fontWeight: '500' }} numberOfLines={3}>
          {props.tagline}
        </Label>
        <Label style={{ fontSize: 15, marginTop: 16, color: popupBodyColor }} numberOfLines={10}>
          {props.body}
        </Label>
        {!!props.examples && <Label style={{ fontSize: 15, marginTop: 8, color: popupBodyColor }}>{props.examples}</Label>}
        <View style={{ marginTop: 24, alignItems: 'flex-start' }}>
          <CustomButton
            style={{ backgroundColor: popupButtonColor, borderColor: popupButtonColor, minWidth: 150 }}
            title={t('got-it')}
            onPress={handleClose}
            textStyle={{ color: '#ffffff', fontSize: 14, fontWeight: '600' }}
          />
        </View>
      </View>
    </View>
  );

  if (mobile) {
    return (
      <ScrollView
        style={[{ flexGrow: 0, width: popupWidth, maxHeight: popupHeight, backgroundColor: popupBackgroundColor }, webNoDragStyle]}
        contentContainerStyle={{ backgroundColor: popupBackgroundColor }}
        nestedScrollEnabled={true}
        scrollEnabled={true}
        bounces={false}
        alwaysBounceVertical={false}
        alwaysBounceHorizontal={false}
        overScrollMode="never"
      >
        <View style={[{ width: popupWidth, backgroundColor: popupBackgroundColor, position: 'relative' }, webNoDragStyle]}>
          {imagesReady ? (
            <>
              <Image
                {...({ pointerEvents: 'none' } as any)}
                source={imageSource}
                style={{ width: popupWidth, height: mobileHeroHeight, resizeMode: 'contain', alignSelf: 'center' }}
              />
              <View style={{ paddingHorizontal: 24, paddingTop: 8, paddingBottom: 24 }}>
                <Label style={{ fontSize: 28, color: '#ffffff', fontWeight: '600', width: 250 }} numberOfLines={5}>
                  {props.title}
                </Label>
                <Label style={{ marginTop: 16, fontSize: 18, color: popupTaglineColor, fontWeight: '500' }} numberOfLines={3}>
                  {props.tagline}
                </Label>
                <Label style={{ fontSize: 15, marginTop: 16, color: popupBodyColor }} numberOfLines={10}>
                  {props.body}
                </Label>
                {!!props.examples && <Label style={{ fontSize: 15, marginTop: 8, color: popupBodyColor }}>{props.examples}</Label>}
                <View style={{ marginTop: 24 }}>
                  <CustomButton
                    style={{ backgroundColor: popupButtonColor, borderColor: popupButtonColor, minWidth: 150 }}
                    title={t('got-it')}
                    onPress={handleClose}
                    textStyle={{ color: '#ffffff', fontSize: 14, fontWeight: '600' }}
                  />
                </View>
              </View>
            </>
          ) : (
            <View style={{ minHeight: 420, backgroundColor: popupBackgroundColor, position: 'relative' as any }}>
              <Loading style={{ backgroundColor: 'transparent' }} />
            </View>
          )}
          <Pressable style={{ position: 'absolute', top: 16, right: 16, zIndex: 1 }} onPress={handleClose}>
            <FontAwesomeIcon icon={faXmark} size={28} color="#000000" />
          </Pressable>
          {renderFooter()}
        </View>
      </ScrollView>
    );
  }

  const content = (
    <View
      style={[
        {
          width: '100%',
          maxWidth: popupMaxWidth,
          height: '100%',
          backgroundColor: popupBackgroundColor,
        },
        webNoDragStyle,
      ]}
    >
      <View style={{ flexGrow: 1, flexShrink: 1, backgroundColor: popupBackgroundColor }}>
        {imagesReady ? (
          renderDesktopBody()
        ) : (
          <View style={{ flexGrow: 1, minHeight: mobile ? 420 : 520, backgroundColor: popupBackgroundColor, position: 'relative' as any }}>
            <Loading style={{ backgroundColor: 'transparent' }} />
          </View>
        )}
        <Pressable style={{ position: 'absolute', top: 16, right: 16, zIndex: 1 }} onPress={handleClose}>
          <FontAwesomeIcon icon={faXmark} size={28} color="#000000" />
        </Pressable>
      </View>
      {renderFooter()}
    </View>
  );

  return (
    <ScrollView
      style={[{ flexGrow: 0, height: popupHeight, width: popupWidth, backgroundColor: popupBackgroundColor }, webNoDragStyle]}
      contentContainerStyle={{ flexGrow: 1 }}
      nestedScrollEnabled={true}
      scrollEnabled={true}
      bounces={false}
      alwaysBounceVertical={false}
      alwaysBounceHorizontal={false}
      overScrollMode="never"
    >
      {content}
    </ScrollView>
  );
};

export default MissionIntroductionPopup;
